import { defineComponent as _defineComponent } from 'vue'
import { onMounted } from 'vue'
import { decode } from 'jsonwebtoken'
import { useRouter } from 'vue-router/composables'

import {
  useSetTokens,
  useAuthenticateWithApple,
  usePendingActions,
} from '@/composables/auth'
import { useMcpAuth } from '@/composables/mcp-auth'
import { useContext } from '@/composables/context'
import { authModule, eventModule } from '@/store'
import { SocialLoginType } from '@/utils/enums'
import { loadScript, logEvent } from '@/utils/utility-manager'

declare global {
  interface Window {
    AppleID: AppleSignInAPI.AppleID
  }
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SingleSignOnApple',
  props: {
    mode: null
  },
  setup(__props: any) {

const props = __props as {
  mode: 'sign_up' | 'sign_in'
};



const { i18n } = useContext()
const router = useRouter()

const { mutate: authenticateWithApple } = useAuthenticateWithApple()
const { setTokens } = useSetTokens()
const { handlePendingActions, getPendingActionsType } = usePendingActions()
const { completeMcpAuth } = useMcpAuth()

onMounted(() => {
  loadScript(
    'appleid-sdk',
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
    () => {
      window.AppleID.auth.init({
        clientId: process.env.APPLE_CLIENT_ID,
        scope: 'email name',
        redirectURI: process.env.DASHBOARD_HOME,
        state: 'init',
        usePopup: true,
      })
    },
  )
})

async function loginApple() {
  try {
    const result = await window.AppleID.auth.signIn()

    const appleUser = decode(result.authorization.id_token, {
      complete: true,
    })
    if (
      typeof appleUser?.payload === 'string' ||
      !appleUser?.payload.email ||
      !appleUser?.payload.sub
    ) {
      return
    }

    const appleData = {
      code: result.authorization.code,
      userId: appleUser.payload.sub,
      identityToken: result.authorization.id_token,
      email: appleUser.payload.email,
      firstName: result.user?.name.firstName,
      lastName: result.user?.name.lastName,
    }

    authenticateWithApple(
      {
        ...appleData,
        locale: i18n.locale as string,
        isDemo: authModule.loggedIn && authModule.user.isDemo,
      },
      {
        async onSuccess(response) {
          logEvent(
            props.mode === 'sign_in' ? 'logged_in' : 'registration_success',
            {
              method: 'apple',
              onboarding_events: getPendingActionsType(),
            },
          )

          if (await completeMcpAuth(response)) return

          setTokens(response, SocialLoginType.APPLE)
          const redirectPath = await handlePendingActions()
          router.push({ path: redirectPath ?? '/' })
        },
      },
    )
  } catch (error) {
    if (isSignInError(error) && error.error === 'popup_closed_by_user') {
      return
    }

    eventModule.newError('error.login.apple')
  }
}

function isSignInError(error: unknown): error is AppleSignInAPI.SignInErrorI {
  return (error as AppleSignInAPI.SignInErrorI).error !== undefined
}

return { __sfc: true,props, i18n, router, authenticateWithApple, setTokens, handlePendingActions, getPendingActionsType, completeMcpAuth, loginApple, isSignInError }
}

})